import { setup, assign } from "xstate";
import type { InkStep } from "@engine/types/narrative.js";

interface NarrativeContext {
  activeKnot: string | null;
  currentStep: InkStep | null;
  /** Portrait key or path for the active NPC; null for trigger/boss knots. */
  portrait: string | null;
  /** instanceId of the party member chosen to speak; null until selected (N03). */
  speakerId: string | null;
}

type NarrativeEvent =
  | { type: "START"; inkKnot: string; portrait?: string; speakerId?: string }
  | { type: "SPEAKER_SELECTED"; instanceId: string }
  | { type: "STEP_READY"; step: InkStep }
  | { type: "CHOICE_SELECTED"; index: number }
  | { type: "CLOSE" };

/**
 * World-freeze contract (N07):
 * NarrativeManager emits "narrative:start" when this machine leaves `inactive`,
 * and "narrative:ended" when it returns to `inactive`. main.tsx subscribes to
 * those events and sends WORLD_FREEZE / WORLD_UNFREEZE to gameMachine so that
 * AI/ATB tickers pause and the OPEN_MENU guard rejects Escape-to-menu while
 * dialogue is showing. Escape instead routes to narrativeManager.close() which
 * sends CLOSE to this machine.
 */
export const narrativeMachine = setup({
  types: {
    context: {} as NarrativeContext,
    events: {} as NarrativeEvent,
  },
}).createMachine({
  id: "narrative",
  initial: "inactive",
  context: { activeKnot: null, currentStep: null, portrait: null, speakerId: null },
  states: {
    inactive: {
      on: {
        START: [
          {
            // Single-member party or auto-selected speaker — skip picker.
            guard: ({ event }) => !!event.speakerId,
            target: "dialogue",
            actions: assign({
              activeKnot: ({ event }) => event.inkKnot,
              currentStep: null,
              portrait: ({ event }) => event.portrait ?? null,
              speakerId: ({ event }) => event.speakerId ?? null,
            }),
          },
          {
            // Multi-member party — show speaker picker first.
            target: "selectingSpeaker",
            actions: assign({
              activeKnot: ({ event }) => event.inkKnot,
              currentStep: null,
              portrait: ({ event }) => event.portrait ?? null,
              speakerId: null,
            }),
          },
        ],
      },
    },
    selectingSpeaker: {
      on: {
        SPEAKER_SELECTED: {
          target: "dialogue",
          actions: assign({ speakerId: ({ event }) => event.instanceId }),
        },
        CLOSE: {
          target: "inactive",
          actions: assign({ activeKnot: null, currentStep: null, portrait: null, speakerId: null }),
        },
      },
    },
    dialogue: {
      on: {
        STEP_READY: {
          actions: assign({ currentStep: ({ event }) => event.step }),
          target: "displayingText",
        },
        CLOSE: "inactive",
      },
    },
    displayingText: {
      on: {
        CHOICE_SELECTED: "dialogue",
        CLOSE: "inactive",
        STEP_READY: {
          // Story ended — no choices, no more content
          guard: ({ event }) => event.step.ended,
          target: "inactive",
        },
      },
    },
  },
});
